/**
 * stepNavSlice — current step + place-order plumbing.
 *
 * Admin-gate is pre-step (see ESC-003) and handled outside this slice.
 * URL syncing is Module 5 (FE lead, Round 2).
 *
 * Phase D-3: also exposes `placeOrderHandler` — the legacy
 * `usePurchaseFlowPlaceOrder().onPlaceOrder` callback published into the
 * store by `LegacyStepShell` so the cart-side "Place order" CTA on the
 * Summary step can fire it directly (no scroll-to-legacy-button bridge).
 *
 * And `stripeConfirmHandler`, published the same way for the same
 * reason. `stripe.confirmPayment` needs the `elements` instance, which only
 * exists inside the Stripe `Elements` scope, while the place-order sequence is
 * driven from outside it. Publishing the confirm step into the store is how the
 * two meet without the place-order handler having to live inside Elements.
 */

import type { StateCreator } from 'zustand'

import type { PfStep } from 'models/purchaseFlowRevamp'
import type { MonetaryValueV2 } from 'models/purchaseFlowRevamp/catalogueV2DTO'
import type { PfStoreState } from '../store'

export type PlaceOrderHandler = () => void

/**
 * Confirms the Stripe payment for an already-placed order. Rejects with the
 * Stripe error so the caller can re-enable its CTA and toast.
 *
 * Resolves with the intent's client secret because `redirect: 'if_required'`
 * usually means Stripe does NOT redirect: the caller has to navigate to
 * `PaymentStatus` itself, and that page reads the payment back by
 * `payment_intent_client_secret`. Without it the client lands on
 * "payment_intent_client_secret parameter is not defined" over an order that
 * was in fact paid (found on the first live run).
 */
export type StripeConfirmHandler = (orderId: number) => Promise<{ clientSecret: string }>

export interface StepNavSliceState {
  currentStep: PfStep
  /**
   * Reactive bridge for the Summary step. Set by `LegacyStepShell` when the
   * legacy `PurchaseFlowPlaceOrderProvider` is mounted; cleared on unmount.
   * Cart-side "Place order" CTA reads this and calls it directly. Falls back
   * to the legacy scroll-to-button behaviour when null.
   */
  placeOrderHandler: PlaceOrderHandler | null
  /**
   * True while the revamp (invoice/v2) place-order POST is in flight. The
   * Summary "Place order" CTA disables on this to prevent a double-submit (a
   * second `POST /workspace/{ws}/orders` → duplicate order). The legacy Stripe
   * path uses its own `orderIsBeingPlaced` signal; this covers the v2 path.
   */
  isPlacingOrder: boolean
  /**
   * Stored filenames the backend reported as no longer kept by the session. Every upload area drops
   * them from its list, so a refused placement cannot leave a file showing as uploaded.
   */
  goneSessionFiles: readonly string[]
  /**
   * Published by the v2 payment-intent provider's confirm component while it is
   * mounted inside Stripe's `Elements` scope; `null` whenever no Stripe method
   * is selected, which is also the signal that a card order cannot be completed
   * yet (no client secret, no Elements).
   */
  stripeConfirmHandler: StripeConfirmHandler | null
  /** The order is placed but Stripe refused its payment; the same button then retries the payment on that order. */
  paymentFailure: PaymentFailure | null
  /**
   * True while the Stripe intent is being opened or re-priced. The Summary CTA
   * disables on it: confirming while a PUT is in flight would charge the amount
   * the client saw a moment ago, not the one on screen.
   */
  isIntentSyncing: boolean
  /**
   * True when opening or re-pricing the Stripe intent FAILED. Distinct from
   * `isIntentSyncing`: the CTA is disabled either way, but the reason the client
   * is given differs — "we could not load the payment form, try again" is
   * actionable, "complete your payment details" is misleading when there is no
   * form to complete. Legacy read this off `paymentIntentMutation.isError` in
   * `usePurchaseFlowOrderMeta`, which the v2 route never wrote.
   */
  isIntentFailed: boolean
  /**
   * GROSS amount of the latest Stripe intent, in major units; `null` while the
   * payment-intent provider is not mounted. Mirrored for the same reason as the
   * two flags above: the cart that must agree with it lives outside that tree.
   */
  intentAmount: MonetaryValueV2 | null
  /**
   * True once Stripe's payment element reports itself complete. The Summary CTA
   * requires it for a card / SEPA cart: firing `confirmPayment` on an incomplete
   * element only produces a Stripe validation error the client has to read, and
   * by then the order has already been placed.
   */
  isStripeFormComplete: boolean
  /** The two halves of `isStripeFormComplete`, so a blocked Place order can say which one is missing; null while
   *  the payment form is not on screen (intent opening or failed). */
  isStripeCardComplete: boolean | null
  isStripeReceiptEmailValid: boolean | null
  /**
   * True once an order has been placed from this flow. Only the EMBED reads it: on its own route
   * the flow navigates away on success (`PaymentStatus`, or the dashboard), but inside the CRM
   * iframe those are main-app destinations — navigating there takes the broker out of the embed
   * with no way back, so the flow stays mounted and shows its terminal confirmation instead.
   *
   * In stepNav rather than the draft slice deliberately: `resetPersistedState` empties the cart on
   * a successful Stripe redirect, and the confirmation must survive that.
   */
  orderPlaced: boolean
}

export interface PaymentFailure {
  orderId: number
  /** Stripe's own message, already in the client's language; empty when there is none (another failure, or a reload). */
  reason: string
}

export interface StepNavSliceActions {
  goToStep(step: PfStep): void
  /** Publish the legacy place-order callback. Pass `null` on unmount. */
  setPlaceOrderHandler(handler: PlaceOrderHandler | null): void
  /** Toggle the revamp place-order in-flight flag (R2 double-submit guard). */
  setPlacingOrder(placing: boolean): void
  /** Report session files the backend no longer stores. */
  markSessionFilesGone(filenames: readonly string[]): void
  /** Publish the Stripe confirm callback. Pass `null` on unmount. */
  setStripeConfirmHandler(handler: StripeConfirmHandler | null): void
  setPaymentFailure(failure: PaymentFailure | null): void
  /** Toggle the intent create/update in-flight flag. */
  setIntentSyncing(syncing: boolean): void
  /** Mirror whether the last intent create/update failed. */
  setIntentFailed(failed: boolean): void
  /** Mirror the latest intent's amount. Pass `null` on unmount. */
  setIntentAmount(amount: MonetaryValueV2 | null): void
  /** Mirror Stripe's `complete` flag off the payment element. */
  setStripeFormComplete(complete: boolean): void
  setStripeCardComplete(complete: boolean | null): void
  setStripeReceiptEmailValid(valid: boolean | null): void
  /** Mark the flow terminal. One-way: nothing in the flow un-places an order. */
  setOrderPlaced(): void
}

export type StepNavSlice = StepNavSliceState & StepNavSliceActions

export const createStepNavSlice: StateCreator<PfStoreState, [], [], StepNavSlice> = (set) => ({
  currentStep: 'catalogue',
  placeOrderHandler: null,
  isPlacingOrder: false,
  goneSessionFiles: [],
  stripeConfirmHandler: null,
  paymentFailure: null,
  isIntentSyncing: false,
  isIntentFailed: false,
  intentAmount: null,
  isStripeFormComplete: false,
  isStripeCardComplete: null,
  isStripeReceiptEmailValid: null,
  orderPlaced: false,

  goToStep: (step) => set({ currentStep: step }),

  setPlaceOrderHandler: (handler) => set((state) => {
    if (state.placeOrderHandler === handler) return state
    return { placeOrderHandler: handler }
  }),

  setPlacingOrder: (placing) => set((state) => (
    state.isPlacingOrder === placing ? state : { isPlacingOrder: placing }
  )),

  markSessionFilesGone: (filenames) => set((state) => {
    const added = filenames.filter((name) => !state.goneSessionFiles.includes(name))
    return added.length === 0 ? state : { goneSessionFiles: [...state.goneSessionFiles, ...added] }
  }),

  setStripeConfirmHandler: (handler) => set((state) => {
    if (state.stripeConfirmHandler === handler) return state
    return { stripeConfirmHandler: handler }
  }),

  setPaymentFailure: (failure) => set({ paymentFailure: failure }),

  setIntentSyncing: (syncing) => set((state) => (
    state.isIntentSyncing === syncing ? state : { isIntentSyncing: syncing }
  )),

  setIntentFailed: (failed) => set((state) => (
    state.isIntentFailed === failed ? state : { isIntentFailed: failed }
  )),

  setIntentAmount: (amount) => set((state) => (
    state.intentAmount?.amount === amount?.amount && state.intentAmount?.currency === amount?.currency
      ? state
      : { intentAmount: amount }
  )),

  setOrderPlaced: () => set((state) => (state.orderPlaced ? state : { orderPlaced: true })),

  setStripeFormComplete: (complete) => set((state) => (
    state.isStripeFormComplete === complete ? state : { isStripeFormComplete: complete }
  )),

  setStripeCardComplete: (complete) => set((state) => (
    state.isStripeCardComplete === complete ? state : { isStripeCardComplete: complete }
  )),

  setStripeReceiptEmailValid: (valid) => set((state) => (
    state.isStripeReceiptEmailValid === valid ? state : { isStripeReceiptEmailValid: valid }
  )),
})
